import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import Label from "../Label/Label";
import CustomButton from "../CustomButton/CustomButton";
import ModalPopup from "../ModalPopup/ModalPopup";
import {
  createCaseStudy,
  deleteCaseStudy,
  fetchCaseStudyById,
  fetchCaseStudyList,
  updateCaseStudy,
  uploadCaseStudiesJson,
} from "../../services/caseStudyApi";
import { assertAdminAuth, getApiErrorMessage } from "../../services/apiClient";
import { getCaseStudyImage } from "../../util/mediaHelpers";
import { buildCaseStudyPath } from "../../util/slugId";
import "./CaseStudyAdminTab.scss";

const CASE_STUDY_TEMPLATE = {
  title: "Fintech",
  subTitle: "Describe the case study in one or two sentences.",
  cardDataOne: {
    heading: "Section heading",
    aboutDataOne: "Overview paragraph",
    listData: [{ id: "1", point: "Key point one" }],
  },
  cardDataTwo: {
    heading: "Second section heading",
    aboutDataTwo: "Second section content",
  },
  cardDataThree: {
    heading: "Third section heading",
    listData: [{ id: "1", point: "Key point one" }],
  },
  cardDataFour: {
    heading: "Fourth section heading",
    aboutDataFour: "Fourth section content",
  },
  afterCardDataFour: {
    heading: "Closing heading",
    para: "Closing paragraph",
  },
  lastData: {
    heading: "Final heading",
    aboutLastData: "Final summary",
    lastDataCard: {
      heading: "Highlights",
      list: [{ id: "1", point: "Highlight one" }],
    },
  },
};

const CaseStudyAdminTab = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCaseStudyId, setEditingCaseStudyId] = useState(null);
  const [caseStudyJson, setCaseStudyJson] = useState(
    JSON.stringify(CASE_STUDY_TEMPLATE, null, 2),
  );
  const [isSaving, setIsSaving] = useState(false);

  const loadCaseStudies = async () => {
    try {
      const items = await fetchCaseStudyList();
      setCaseStudies(items);
    } catch (error) {
      console.error("Failed to load case studies", error);
    }
  };

  useEffect(() => {
    loadCaseStudies();
  }, []);

  const openCreateModal = () => {
    setEditingCaseStudyId(null);
    setCaseStudyJson(JSON.stringify(CASE_STUDY_TEMPLATE, null, 2));
    setShowModal(true);
  };

  const openEditModal = async (caseStudyId) => {
    setIsSaving(true);

    try {
      const caseStudy = await fetchCaseStudyById(caseStudyId);
      setEditingCaseStudyId(caseStudyId);
      setCaseStudyJson(JSON.stringify(caseStudy, null, 2));
      setShowModal(true);
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to load case study."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCaseStudy = async () => {
    if (!assertAdminAuth()) {
      return;
    }

    setIsSaving(true);

    try {
      const payload = JSON.parse(caseStudyJson);

      if (!payload.title?.trim() || !payload.subTitle?.trim()) {
        window.alert("Case study JSON must include title and subTitle.");
        return;
      }

      if (editingCaseStudyId) {
        await updateCaseStudy(editingCaseStudyId, payload);
      } else {
        await createCaseStudy(payload);
      }

      setShowModal(false);
      await loadCaseStudies();
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to save case study."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCaseStudy = async (caseStudyId) => {
    if (!assertAdminAuth()) {
      return;
    }

    if (!window.confirm("Delete this case study?")) {
      return;
    }

    try {
      await deleteCaseStudy(caseStudyId);
      await loadCaseStudies();
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to delete case study."));
    }
  };

  const handleJsonUpload = async (event) => {
    if (!assertAdminAuth()) {
      event.target.value = "";
      return;
    }

    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setIsSaving(true);

    try {
      const fileText = await file.text();
      const parsed = JSON.parse(fileText);
      const items = Array.isArray(parsed) ? parsed : parsed.items;

      if (!Array.isArray(items) || !items.length) {
        window.alert("JSON must contain a non-empty array of case studies.");
        return;
      }

      await uploadCaseStudiesJson(items);
      await loadCaseStudies();
      window.alert("Case studies uploaded successfully.");
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to upload case studies JSON."));
    } finally {
      setIsSaving(false);
      event.target.value = "";
    }
  };

  return (
    <div className="caseStudy_Admin_Tab">
      <div className="caseStudy_Admin_Header">
        <Label title="Case Study Management" className="heading" />
        <div className="caseStudy_Admin_Actions">
          <Form.Group controlId="caseStudyJsonUpload" className="upload_Group">
            <Form.Control
              type="file"
              accept=".json,application/json"
              onChange={handleJsonUpload}
            />
          </Form.Group>
          <CustomButton title="Add Case Study" onClick={openCreateModal} />
        </div>
      </div>

      <div className="caseStudy_Admin_Grid">
        {caseStudies.map((item) => (
          <div className="caseStudy_Admin_Card" key={item.id}>
            <img
              src={getCaseStudyImage(item.title)}
              alt={item.title}
              className="caseStudy_Admin_Image"
            />
            <div className="caseStudy_Admin_Content">
              <Label title={item.title} className="heading" />
              <Label title={item.subTitle} className="desc" />
              <div className="caseStudy_Admin_Links">
                <span className="route_Text">{buildCaseStudyPath(item.id)}</span>
              </div>
              <div className="caseStudy_Admin_Buttons">
                <CustomButton
                  title="Edit"
                  onClick={() => openEditModal(item.id)}
                />
                <CustomButton
                  title="Delete"
                  onClick={() => handleDeleteCaseStudy(item.id)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <ModalPopup
        showModal={showModal}
        onHide={() => setShowModal(false)}
        onHideClick={() => setShowModal(false)}
        onButtonClick={handleSaveCaseStudy}
        headerTitle={
          editingCaseStudyId ? "Edit Case Study JSON" : "Add Case Study JSON"
        }
        buttonTitle={isSaving ? "Saving..." : "Save Case Study"}
      >
        <div className="caseStudy_Json_Form">
          <Form.Control
            as="textarea"
            rows={18}
            value={caseStudyJson}
            onChange={(event) => setCaseStudyJson(event.target.value)}
          />
        </div>
      </ModalPopup>
    </div>
  );
};

export default CaseStudyAdminTab;
